import { useState, useEffect, useCallback } from 'react';
import { collection, query, limit as fsLimit, getDocs, orderBy as fsOrderBy, where, type WhereFilterOp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface CollectionDataResult {
  data: any[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

type CollectionFilter = {
  field: string;
  op: WhereFilterOp;
  value: unknown;
};

export function useCollectionData(
  collectionName: string,
  limitCount: number,
  orderByField: string,
  filter?: CollectionFilter | null,
): CollectionDataResult {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCollection = useCallback(async (getIsMounted: () => boolean) => {
    if (!collectionName) {
      if (getIsMounted()) setLoading(false);
      return;
    }
    
    if (getIsMounted()) {
      setLoading(true);
      setError(null);
    }
    
    try {
      const collRef = collection(db, collectionName);
      let q = query(collRef, fsOrderBy(orderByField, 'desc'));
      if (filter) {
        q = query(collRef, where(filter.field, filter.op, filter.value), fsOrderBy(orderByField, 'desc'));
      }
      
      if (limitCount > 0) {
        q = query(q, fsLimit(limitCount));
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (getIsMounted()) {
        setData(docs);
      }
    } catch (err: any) {
      console.error(`Error fetching ${collectionName}:`, err);
      if (getIsMounted()) {
        setError(err.message || 'Failed to fetch data');
      }
    } finally {
      if (getIsMounted()) {
        setLoading(false);
      }
    }
  }, [collectionName, filter?.field, filter?.op, filter?.value, limitCount, orderByField]);

  const refresh = useCallback(async () => {
    await fetchCollection(() => true);
  }, [fetchCollection]);

  useEffect(() => {
    let isMounted = true;
    fetchCollection(() => isMounted);
    return () => {
      isMounted = false;
    };
  }, [fetchCollection]);

  return { data, loading, error, refresh };
}
